import React from 'react';
import { SyncStatus } from '@/hooks/useOutlookSyncProgress';
import { Mail, Check, X, CloudDownload } from 'lucide-react';
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/lib/utils";
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import * as HoverCard from '@radix-ui/react-hover-card';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { useIManageSync } from '@/hooks/useIManageSync';
import { useOneDriveSync } from '@/hooks/useOneDriveSync';
import { toolbarActionLabelClass, toolbarCompactOnlyClass, toolbarExpandedFlexClass } from '@/lib/toolbar-compact';
import Loading from './Loading';
import { MicrosoftProviderIcon } from './MicrosoftProviderIcon';

interface OutlookSyncProgressProps {
  status: SyncStatus;
  matterId?: string;
  onDismiss?: () => void;
}

interface SyncWithOutlookButtonProps {
  matterId: string;
}

// Enhanced animated progress specifically for sync operations
const AnimatedProgress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    isActive?: boolean;
  }
>(({ className, value, isActive = false, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative h-1 w-full overflow-hidden rounded-full bg-blue-100",
      className
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(
        "h-full w-full flex-1 transition-all duration-500 relative overflow-hidden",
        isActive
          ? "bg-linear-to-r from-blue-300 via-blue-500 to-blue-300 bg-size-[400%_auto]"
          : "bg-blue-500"
      )}
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    >
      {isActive && (
        <div className="absolute inset-0 w-1/3 h-full bg-linear-to-r from-transparent via-white/20 to-transparent" />
      )}
    </ProgressPrimitive.Indicator>
  </ProgressPrimitive.Root>
));
AnimatedProgress.displayName = "AnimatedProgress";

export function OutlookSyncProgress({ status, matterId, onDismiss }: OutlookSyncProgressProps) {
  const navigate = useNavigate();
  const isActive = status.status === 'IN_PROGRESS' || status.status === 'STARTED';

  // Add counter animation effect for percentage
  const [displayProgress, setDisplayProgress] = React.useState(status.progress);

  React.useEffect(() => {
    // Animate the progress count
    if (displayProgress !== status.progress) {
      const timer = setTimeout(() => {
        if (displayProgress < status.progress) {
          setDisplayProgress(prev => Math.min(prev + 1, status.progress));
        } else if (displayProgress > status.progress) {
          setDisplayProgress(prev => Math.max(prev - 1, status.progress));
        }
      }, 20);

      return () => clearTimeout(timer);
    }
  }, [displayProgress, status.progress]);

  // Determine icon based on status
  const renderCompactIcon = () => {
    return (
      <div className="relative">
        <Mail className="h-6 w-6 text-gray-600" />
        {status.status === 'COMPLETED' && (
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full">
            <Check className="h-4 w-4 text-green-500" strokeWidth={3} />
          </div>
        )}
        {status.status === 'FAILED' && (
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full">
            <X className="h-4 w-4 text-red-500" strokeWidth={3} />
          </div>
        )}
        {isActive && (
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full">
            <Loading />
          </div>
        )}
      </div>
    );
  };

  // Only show progress component if there's an active sync
  if (status.status === 'NO_SYNC') {
    return null;
  }

  return (
    <HoverCard.Root openDelay={300}>
      <HoverCard.Trigger asChild>
        <button
          className="cursor-pointer focus:outline-hidden flex items-center justify-center"
          onClick={() => matterId && navigate({ to: '/matter/$slug/sync', params: { slug: matterId } })}
        >
          {renderCompactIcon()}
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          className="z-50 w-80"
          sideOffset={5}
        >
          <div className={`px-3 py-2 rounded-md border bg-gray-50 border-gray-200 shadow-lg`}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {status.status === 'COMPLETED' ?
                    "Synced with Outlook" :
                    status.status === 'FAILED' ?
                    "Outlook sync failed" :
                    "Syncing with Outlook..."
                  }
                </span>
                <span className="text-xs text-gray-500 tabular-nums">{displayProgress}%</span>
              </div>
              <AnimatedProgress value={status.progress} isActive={isActive} />
              <span className="text-xs text-gray-500">{status.description}</span>
            </div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}

export function SyncWithOutlookButton({ matterId }: SyncWithOutlookButtonProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate({ to: '/matter/$slug/sync', params: { slug: matterId } });
  };

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      size="sm"
    >
      <Mail className="h-4 w-4" />
      Connect to Outlook
    </Button>
  );
}

interface ConnectToIManageButtonProps {
  matterId: string;
}

export function ConnectToIManageButton({ matterId }: ConnectToIManageButtonProps) {
  const navigate = useNavigate();
  const { shouldShowButton } = useIManageSync(matterId);

  const handleClick = () => {
    navigate({ to: '/matter/$slug/sync', params: { slug: matterId } });
  };

  // Don't show the button if conditions aren't met
  if (!shouldShowButton) {
    return null;
  }

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      size="sm"
    >
      <img
        src="/iManage-logo.png"
        alt="iManage logo"
        className="h-4 w-4 object-contain"
      />
      Connect to iManage
    </Button>
  );
}

interface ConnectToOneDriveButtonProps {
  matterId: string;
}

export function ConnectToOneDriveButton({ matterId }: ConnectToOneDriveButtonProps) {
  const navigate = useNavigate();
  const { shouldShowButton, connectLabel, iconProviders } = useOneDriveSync(matterId);

  const handleClick = () => {
    navigate({ to: '/matter/$slug/sync', params: { slug: matterId } });
  };

  if (!shouldShowButton) {
    return null;
  }

  return (
    <Button onClick={handleClick} variant="outline" size="sm">
      {iconProviders.map((provider) => (
        <MicrosoftProviderIcon key={provider} provider={provider} />
      ))}
      {connectLabel}
    </Button>
  );
}

interface ConnectSourcesControlsProps {
  matterId: string;
}

// Join enabled source names as "A, B, C or D".
function formatSourceList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

export function ConnectSourcesControls({ matterId }: ConnectSourcesControlsProps) {
  const navigate = useNavigate();
  const { isEnabled: iManageEnabled } = useFeatures(FEATURE_FLAGS.IMANAGE);
  const { isEnabled: oneDriveEnabled } = useFeatures(FEATURE_FLAGS.ONEDRIVE);
  const { isEnabled: sharePointEnabled } = useFeatures(FEATURE_FLAGS.SHAREPOINT);

  // Enabled sync sources, in display order. Outlook is always available
  // (mirrors AddSyncDialog, where only iManage/OneDrive/SharePoint are gated).
  const sources: { name: string; icon: React.ReactNode }[] = [
    ...(iManageEnabled
      ? [{
          name: 'iManage',
          icon: (
            <img src="/iManage-logo.png" alt="iManage logo" className="h-4 w-4 object-contain" />
          ),
        }]
      : []),
    ...(oneDriveEnabled
      ? [{ name: 'OneDrive', icon: <MicrosoftProviderIcon provider="onedrive" /> }]
      : []),
    ...(sharePointEnabled
      ? [{ name: 'SharePoint', icon: <MicrosoftProviderIcon provider="sharepoint" className="h-3.5" /> }]
      : []),
    {
      name: 'Outlook',
      icon: <img src="/microsoft-outlook.svg" alt="Outlook logo" className="h-4 w-4 object-contain" />,
    },
  ];

  // Multiple sources: generic import glyph. Single source: that source's own icon.
  const leadingIcon = sources.length > 1
    ? <CloudDownload className="h-4 w-4" />
    : sources[0].icon;

  const tooltipText = `Sync from ${formatSourceList(sources.map((s) => s.name))}`;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/matter/$slug/sync', params: { slug: matterId } })}
            aria-label={tooltipText}
          >
            <span className={cn('flex items-center', toolbarCompactOnlyClass)}>{leadingIcon}</span>
            <span className={cn(toolbarExpandedFlexClass, 'items-center gap-1.5')}>
              {sources.map((source) => (
                <span key={source.name} className="flex items-center">
                  {source.icon}
                </span>
              ))}
            </span>
            <span className={toolbarActionLabelClass}>Sync</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs font-normal">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}